import React, {useState} from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {TscStyles, TscThemeNames, TscThemeName} from '@techsmith/tsc-cloud-style-guide';
import withMemoizedContexts from '../../context/contextContainerHoC';
import ReactionsSummaryPopup from './ReactionsSummaryPopup';
import {themeStore} from '../../context/themeProvider';
import {getNativeEmojiById} from '../../util/EmojiSearch';
import {reactionsStore} from '../../context/reactionsProvider';
import {IEmojiReactionData} from '../../model/emojiReactionsModel';
import Constants from '../../constants/Constants';
import ReactionUIButton from './ReactionUIButton';
import CssUtil from '../../util/CssUtil';

const reactionsSummaryPopupLeftOffset = 0;
const reactionsSummaryPopupBottomOffset = 40;
const backgroundColorOpacity = 0.75;

const ReactionsImageWidgetContainer = styled.div<{isEmbedded: boolean}>`
   display: flex;
   position: ${props => props.isEmbedded ? 'absolute' : 'relative'};
   ${props => props.isEmbedded && 'top: 0.5rem'};
   ${props => props.isEmbedded && 'right: 3rem'};
   ${props => props.isEmbedded && 'pointer-events: none'};
   ${props => props.isEmbedded && `background-color: ${CssUtil.toRgba(TscStyles.color.ui.dusk.mediumDark, backgroundColorOpacity)}`};
   ${props => props.isEmbedded && 'backdrop-filter: blur(4px)'};
   ${props => props.isEmbedded && `border-radius: ${TscStyles.border.radius.md}`};
`;

const EmojiContainer = styled.div<{containerWidth: number}>`
   position: relative;
   width: ${props => props.containerWidth}px;
   height: 16px;
   line-height: 1.25rem;
   margin-left: .25rem!important;
`;

const EmojiWrapper = styled.div<{leftOffset: number}>`
   display: inline-block;
   position: absolute;
   left: ${props => props.leftOffset}px;
`;

const ReactionCount = styled.div<{isEmbedded: boolean; theme: TscThemeNames}>`
   padding: 0 .5rem!important;
   color: ${props => props.theme === TscThemeName.dusk || props.isEmbedded ? TscStyles.color.text.light : TscStyles.color.text.dark};
`;

export const ReactionsImageWidgetBase: React.FC<IReactionsImageWidgetProps & IStateMappedProps> = ({isEmbedded, theme, reactions}) => {
   const {t} = useTranslation();
   const numberEmojiToPreview = Math.min(reactions.length, Constants.reactionMarkers.maxNumberEmojiToPreview);
   const markerWidth = Constants.reactionMarkers.emojiWidth + (Constants.reactionMarkers.emojiOffset * (numberEmojiToPreview - 1));
   const [showSummary, setShowSummary] = useState(false);

   const getNewestEmoji = (): React.ReactFragment => {
      const previewReactionList = reactions.slice(reactions.length - numberEmojiToPreview, reactions.length);
      return previewReactionList.map((reaction, index) => {
         const posIndex = numberEmojiToPreview - (index + 1);
         return (
            <EmojiWrapper
               key={posIndex}
               leftOffset={posIndex * Constants.reactionMarkers.emojiOffset}
            >
               {getNativeEmojiById(reaction.EmojiId)}
            </EmojiWrapper>
         );
      });
   };

   const onToggleSummary = () => {
      const summaryDisplayState = !showSummary;
      setShowSummary(summaryDisplayState);
   };

   const onReactionSummaryClose = () => {
      setShowSummary(false);
   };

   return reactions.length > 0 && (
      <ReactionsImageWidgetContainer isEmbedded={isEmbedded}>
         <ReactionUIButton
            isEmbedded={isEmbedded}
            isActive={showSummary}
            isDisabled={isEmbedded}
            height={2.25}
            fontSize={0.875}
            padding="0 .5rem"
            margin={isEmbedded ? '0' : '0 .5rem 0 0'}
            borderRadius={TscStyles.border.radius.md}
            hasBorder={!isEmbedded}
            title={showSummary ? t('conversations.reactionHideImageButtonTitle') : t('conversations.reactionShowImageButtonTitle')}
            ariaLabel={showSummary ? t('conversations.reactionHideImageButtonTitle') : t('conversations.reactionShowImageButtonTitle')}
            onClick={onToggleSummary}
         >
            <EmojiContainer containerWidth={markerWidth}>
               {getNewestEmoji()}
            </EmojiContainer>
            <ReactionCount
               isEmbedded={isEmbedded}
               theme={theme}
            >
               {reactions.length}
            </ReactionCount>
         </ReactionUIButton>
         {showSummary && (
            <ReactionsSummaryPopup
               reactionsToDisplay={reactions}
               leftOffset={reactionsSummaryPopupLeftOffset}
               bottomOffset={reactionsSummaryPopupBottomOffset}
               maxHeight={Constants.reactionsSummary.maxHeight}
               closeOnInteractions={false}
               onClose={onReactionSummaryClose}
            />
         )}
      </ReactionsImageWidgetContainer>
   );
};

export interface IReactionsImageWidgetProps {
   isEmbedded: boolean;
}

export interface IStateMappedProps {
   theme: TscThemeNames;
   reactions: IEmojiReactionData[];
}

const mapStatesToProps = (
   {isEmbedded}: IReactionsImageWidgetProps,
   {theme}: Partial<IStateMappedProps>,
   {reactions}: Partial<IStateMappedProps>
): IStateMappedProps & IReactionsImageWidgetProps => ({isEmbedded, theme, reactions});

export default withMemoizedContexts(mapStatesToProps, themeStore, reactionsStore)(ReactionsImageWidgetBase);
